import React, { useState } from "react";

import { LuEye } from "react-icons/lu";
import { LuEyeOff } from "react-icons/lu";
import classes from "./input.module.css";
import { mergeClass } from "../../utils/helper";
import { AttachmentIcon, AttachmentIconWhite } from "@/constant/imagePath";
import { cn } from "@/utils/cn";

/**
 * Primary UI component for user interaction
 */
const Input = ({
  type = "",
  label,
  value, // input value
  setValue, //setValue
  noBorder,
  placeholder,
  disabled,
  customStyle, //Input Container inline Style
  inputStyle, //Input inline Style
  labelStyle, //Label inline Style
  error, // Show Error Boolean
  errorText, // Error Text
  leftIcon, // Icon For Input
  rightIcon, // Icon For Input
  inputRef,
  onEnterClick,
  className,
  inputContainerClass = "",
  variant = "",
  labelClass = "",
  autoComplete,
  forgotPassword,
  rightIconCustom,
  errorTextClass,
  onChange = null,
  showRainbowBorder,
  inputBoxDiv,
  showRightIconGradient,
  hint = "",
  required = true,
  ...props
}) => {
  const [passToggle, setPassToggle] = useState(false);

  return (
    <div>
      <div
        className={`${[classes.Container, className && className].join(" ")}`}
        data-variant={variant}
      >
        {label && (
          <label
            htmlFor={`input${label}`}
            className={`${[
              classes.labelText,
              disabled && classes.disabled,
              labelClass,
            ].join(" ")}`}
            style={{ ...labelStyle }}
          >
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div
          className={`${[
            classes.inputPassContainer,
            inputContainerClass,
            value && showRainbowBorder && classes.isTyping,
            showRainbowBorder && classes.inputRainbowBorder,
            disabled && classes.disabled,
            type === "file" && "border-0",
          ].join(" ")}`}
          style={{ ...customStyle }}
        >
          {leftIcon && (
            <div
              className={mergeClass(
                value && classes.rightBorder,
                classes.leftIconBox,
                leftIcon.props?.parentClassNames || '',
              )}
            >
              {leftIcon}
            </div>
          )}
          {type === "file" ? (
            <div className="flex items-center justify-between space-x-2 h-8 w-full">
              {value instanceof FileList && value.length > 0 ? (
                <ul className="self-baseline text-jersium-black text-sm">
                  {Array.from(value).map((file, idx) => (
                    <li key={idx}>{file.name}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-jersium-black text-sm truncate">
                  {value?.name ?? "No file chosen"}
                </div>
              )}
              <input
                id={`file${label}`}
                type="file"
                className="hidden"
                onChange={(e) => {
                  if (onChange) {
                    onChange(e);
                  } else {
                    setValue(e.target.files[0]);
                  }
                }}
                multiple={props.multiple}
                accept={props.accept}
              />
              <label
                htmlFor={`file${label}`}
                className="h-full text-sm flex flex-shrink-0 text-nowrap bg-green-black-gradient items-center gap-x-2 cursor-pointer px-2 py-1 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-primary-inset"
              >
                <img
                  src={AttachmentIconWhite}
                  className="h-3"
                  alt="Attachment icon"
                />
                Choose File
              </label>
            </div>
          ) : type === "textarea" ? (
            <textarea
              value={value ?? ""}
              onChange={(e) => {
                if (onChange) {
                  onChange(e);
                } else {
                  setValue(e.target.value);
                }
              }}
              disabled={disabled}
              placeholder={placeholder}
              id={`textarea${label}`}
              className={`resize-none p-2 ${[
                inputBoxDiv,
                classes.inputBox,
                leftIcon && classes.inputPadding,
                noBorder && classes.noBorder,
                disabled && classes.disabledPlaceholder,
              ].join(" ")}`}
              style={{ ...inputStyle, height: "initial" }}
              onBlur={() => setValue(value?.trim())}
              ref={inputRef}
              {...props}
            />
          ) : (
            <input
              value={value ?? ""}
              onChange={(e) => {
                if (onChange) {
                  onChange(e); // Call onChange if defined
                } else {
                  let newValue = e.target.value;
                  if (type === "number") {
                    newValue = newValue.replace(/^0+(?=\d)/, ""); // remove 0 at the beginning
                  }
                  setValue(newValue); // Use setValue if onChange is not defined
                }
              }}
              disabled={disabled}
              autoComplete={autoComplete}
              placeholder={placeholder}
              type={passToggle == true ? "text" : type}
              id={`input${label}`}
              className={` ${[
                inputBoxDiv,
                classes.inputBox,
                leftIcon && classes.inputPadding,
                noBorder && classes.noBorder,
                disabled && classes.disabledPlaceholder,
              ].join(" ")}`}
              style={{ ...inputStyle }}
              onKeyPress={(e) =>
                ["Enter", "NumpadEnter"].includes(e.code) &&
                onEnterClick &&
                onEnterClick()
              }
              onBlur={() => {
                if (type === "text" || type === "textarea") {
                  setValue(value?.trim());
                }
              }}
              ref={inputRef}
              {...props}
              onKeyDown={(e) => {
                if (type == "number") {
                  return (
                    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
                  );
                }
              }}
            />
          )}

          {type === "password" && (
            <div className={classes.passwordDiv}>
              {passToggle == false && (
                <LuEyeOff
                  color="var(--blue-dark)"
                  className={mergeClass(
                    forgotPassword ? "" : classes.passwordIcon
                  )}
                  size={20}
                  onClick={() => setPassToggle(!passToggle)}
                />
              )}
              {passToggle && (
                <LuEye
                  color="var(--blue-dark)"
                  className={mergeClass(
                    forgotPassword ? "" : classes.passwordIcon
                  )}
                  size={20}
                  onClick={() => setPassToggle(!passToggle)}
                />
              )}
              {forgotPassword && forgotPassword}
            </div>
          )}
        </div>
        {rightIcon && (
          <div
            className={`${[
              rightIconCustom,
              classes.rightIconBox,
              showRightIconGradient && classes.showRightIconBorder,
              value && showRightIconGradient && showRightIconGradient,
            ].join(" ")}`}
          >
            {rightIcon}
          </div>
        )}
      </div>
      {hint && (
        <div
          className={cn("relative flex-1", className)}
          data-variant={variant}
        >
          <div></div>
          <div className="mt-2 text-info-gray w-[450px]">{hint}</div>
        </div>
      )}
      {errorText && (
        <div
          className={`${[classes.Container, className && className].join(" ")}`}
          data-variant={variant}
        >
          <div></div>
          <div
            className={`mt-2 ${[classes.errorText, errorTextClass].join(" ")}`}
          >
            {errorText + "*"}
          </div>
        </div>
      )}
    </div>
  );
};

export default Input;
